import { Component, computed, DestroyRef, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DynamicRouteHandlingService, GenericDropdownComponent, InputFieldComponent } from '@zayan-erp/shared';
import { GeographyService } from '../../services/geography.service';
import { signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TaxAuthorityService } from '../../services/tax-authority.service';
import { tap } from 'rxjs';
import { of } from 'rxjs';
import { TaxAuthority } from '../../models/tax-authority.models';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-tax-authority-form',
  imports: [InputFieldComponent, FormsModule, ReactiveFormsModule, GenericDropdownComponent, ToggleSwitchModule],
  templateUrl: './tax-authority-form.component.html'
})
export class TaxAuthorityFormComponent {
  #geographyService = inject(GeographyService);
  #activatedRoute = inject(ActivatedRoute);
  #taxAuthorityService = inject(TaxAuthorityService);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #destroyRef = inject(DestroyRef);

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.submit());
  }

  selectedTaxAuthorityId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedTaxAuthorityId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#taxAuthorityService.getTaxAuthorityById(params.id).pipe(
          tap(taxAuthority => {
            this.editForm(taxAuthority);
          })
        );
      }
      return of(null);
    }
  });

  editForm(taxAuthority: TaxAuthority) {
    this.taxAuthorityForm.patchValue({
      name: taxAuthority.name,
      code: taxAuthority.code,
      countryId: taxAuthority.countryId,
      provinceId: taxAuthority.provinceId,
      regNoExempt: taxAuthority.regNoExempt
    });
  }

  taxAuthorityForm = new FormGroup({
    code: new FormControl<string | null>(null, [Validators.required]),
    name: new FormControl<string | null>(null),
    countryId: new FormControl<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('countryId') ?? null),
    provinceId: new FormControl<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('provinceId') ?? null),
    regNoExempt: new FormControl<boolean>(false)
  });
  countries = toSignal(this.#geographyService.getCountries(), { initialValue: [] });
  provinces = toSignal(this.#geographyService.getAllProvinces(), { initialValue: [] });
  countriesList = computed(() =>
    this.countries().map(country => ({ id: country.id, firstLabel: country.name, secondLabel: country.code }))
  );
  countryId = toSignal(this.taxAuthorityForm.get('countryId')!.valueChanges, {
    initialValue: this.#activatedRoute.snapshot.queryParamMap.get('countryId') ?? null
  });
  provincesList = computed(() => {
    return this.provinces()
      .filter(province => province.country?.id === this.countryId()!)
      .map(province => ({ id: province.id, firstLabel: province.name, secondLabel: province.provCode }));
  });

  submit() {
    if (this.taxAuthorityForm.invalid) {
      this.taxAuthorityForm.markAllAsTouched();
      return;
    }
    if (this.selectedTaxAuthorityId()) {
      this.#taxAuthorityService
        .updateTaxAuthority(this.selectedTaxAuthorityId()!, this.taxAuthorityForm.value as TaxAuthority)
        .subscribe({
          next: () => this.afterSubmitSuccess()
        });
    } else {
      this.#taxAuthorityService.createTaxAuthority(this.taxAuthorityForm.value as TaxAuthority).subscribe({
        next: () => this.afterSubmitSuccess()
      });
    }
  }
  afterSubmitSuccess() {
    this.taxAuthorityForm.reset();
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
  }
}
